import React from 'react';
import { FileText, Building, Users, RefreshCw } from 'lucide-react';
import { Contract } from '@app-types';
import { formatCurrency } from '@utils/currencyUtils';
import { formatMediumCalendarDate } from '@utils/dateUtils';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import Tooltip from '@components/common/Tooltip';
import { contractStatusToVariant, tierToVariant } from '@utils/badgeMappers';
import { InfoCard, InfoCardGrid, SupplierLink } from '@components/common';
import TierBadge from '@components/common/TierBadge';
import styles from './ContractInfo.module.css';

interface ContractInfoProps {
  contract: Contract;
  assessmentsCount?: number;
  activeAssessmentsCount?: number;
  onViewSupplier?: (supplierId: number) => void;
  onChangeSupplier?: () => void;
  canChangeSupplier?: boolean;
}

const ContractInfo: React.FC<ContractInfoProps> = ({
  contract,
  assessmentsCount = 0,
  activeAssessmentsCount = 0,
  onViewSupplier,
  onChangeSupplier,
  canChangeSupplier = false
}) => {
  return (
    <div className={styles.contractInfoSimple}>
      <InfoCardGrid>
        {/* Basic Info Card */}
        <InfoCard>
          <InfoCard.Header>
            <InfoCard.Title icon={<FileText size={20} />}>Contract Information</InfoCard.Title>
          </InfoCard.Header>
          <InfoCard.Content>
            <InfoCard.Row>
              <InfoCard.Label>Contract Number:</InfoCard.Label>
              <InfoCard.Value>{contract.contract_number}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Name:</InfoCard.Label>
              <InfoCard.Value>{contract.name}</InfoCard.Value>
            </InfoCard.Row>
            {contract.description && (
              <InfoCard.Row>
                <InfoCard.Label>Description:</InfoCard.Label>
                <InfoCard.Value>{contract.description}</InfoCard.Value>
              </InfoCard.Row>
            )}
            <InfoCard.Row>
              <InfoCard.Label>Status:</InfoCard.Label>
              <Badge variant={contractStatusToVariant(contract.status)}>{contract.status}</Badge>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Start Date:</InfoCard.Label>
              <InfoCard.Value>{formatMediumCalendarDate(contract.start_date, '-')}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract End Date:</InfoCard.Label>
              <InfoCard.Value>{formatMediumCalendarDate(contract.end_date, '-')}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Amount:</InfoCard.Label>
              <InfoCard.Value>{formatCurrency(contract.amount || contract.value)}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Type:</InfoCard.Label>
              <InfoCard.Value>{contract.contract_type || '-'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Cost Center:</InfoCard.Label>
              <InfoCard.Value>{contract.cost_center || '-'}</InfoCard.Value>
            </InfoCard.Row>
            {contract.tier && (
              <InfoCard.Row>
                <InfoCard.Label>Tier:</InfoCard.Label>
                <TierBadge variant={tierToVariant(contract.tier)}>{contract.tier}</TierBadge>
              </InfoCard.Row>
            )}
            <InfoCard.Row>
              <InfoCard.Label>Scope:</InfoCard.Label>
              <InfoCard.Value>{contract.assessment_scope}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>PII Access:</InfoCard.Label>
              <InfoCard.Value>{contract.pii_access ? 'Yes' : 'No'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>PHI Access:</InfoCard.Label>
              <InfoCard.Value>{contract.phi_access ? 'Yes' : 'No'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>BCI Access:</InfoCard.Label>
              <InfoCard.Value>{contract.bci_access ? 'Yes' : 'No'}</InfoCard.Value>
            </InfoCard.Row>
          </InfoCard.Content>
        </InfoCard>

        {/* Team & Supplier Card */}
        <InfoCard>
          <InfoCard.Header>
            <InfoCard.Title icon={<Users size={20} />}>Team & Supplier</InfoCard.Title>
          </InfoCard.Header>
          <InfoCard.Content>
            <InfoCard.Row>
              <InfoCard.Label>Supplier Name:</InfoCard.Label>
              <InfoCard.ValueWithAction>
                {contract.supplier_name ? (
                  <SupplierLink icon={<Building size={16} />} onClick={() => onViewSupplier?.(contract.supplier_id)}>
                    {contract.supplier_name}
                  </SupplierLink>
                ) : (
                  <InfoCard.Value>No supplier assigned</InfoCard.Value>
                )}
                {canChangeSupplier && onChangeSupplier && (
                  <Tooltip
                    content={`Cannot reassociate contract - has ${activeAssessmentsCount} active assessment${
                      activeAssessmentsCount !== 1 ? 's' : ''
                    }. Complete or cancel them first.`}
                    position="top"
                    disabled={activeAssessmentsCount === 0}
                    width="200px"
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={onChangeSupplier}
                      disabled={activeAssessmentsCount > 0}
                      title={activeAssessmentsCount === 0 ? 'Change Supplier' : undefined}
                      dataTestId="contract-change-supplier-btn"
                    >
                      <RefreshCw size={14} />
                      Change
                    </Button>
                  </Tooltip>
                )}
              </InfoCard.ValueWithAction>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Division:</InfoCard.Label>
              <InfoCard.Value>{contract.division_name || contract.division || '-'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Division Lead:</InfoCard.Label>
              <InfoCard.Value>{contract.division_lead_name || '-'}</InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Owner:</InfoCard.Label>
              <InfoCard.Value>
                {contract.contract_owners && contract.contract_owners.length > 0
                  ? contract.contract_owners.map((o) => o.name).join(', ')
                  : 'Unassigned'}
              </InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Contract Manager:</InfoCard.Label>
              <InfoCard.Value>
                {contract.contract_managers && contract.contract_managers.length > 0
                  ? contract.contract_managers.map((m) => m.name).join(', ')
                  : 'Unassigned'}
              </InfoCard.Value>
            </InfoCard.Row>
            <InfoCard.Row>
              <InfoCard.Label>Total Assessments:</InfoCard.Label>
              <InfoCard.Value>{assessmentsCount}</InfoCard.Value>
            </InfoCard.Row>
          </InfoCard.Content>
        </InfoCard>
      </InfoCardGrid>
    </div>
  );
};

export default ContractInfo;
